<template>
  <div class="rd-wrapper">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    
    <v-snackbar v-model="showSnackBar" :color="primaryColor" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <!-- ══ HEADER BANNER ══ -->
    <div class="rd-header" :style="{ background: gradientBackground }">
      <div class="rd-header__title">
        <v-btn icon color="white" class="mr-2" @click="$router.push(backPath)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        Rescuer Analytics Dashboard
      </div>
    </div>

    <!-- ══ CONTENT ══ -->
    <div class="rd-content pa-6">
      
      <!-- Top Cards Grid -->
      <v-row class="mb-6">
        <!-- Profile Card -->
        <v-col cols="12" lg="6">
          <v-card class="profile-card pa-6 elevation-0 height-equalizer d-flex align-center">
            <v-avatar size="80" :color="avatarColor" class="mr-6 font-weight-bold" :class="avatarText" style="font-size: 28px;">
              {{ (user && user.name) ? user.name.charAt(0).toUpperCase() : 'U' }}
            </v-avatar>
            <div>
              <h2 class="rescuer-name mb-1">{{ (user && user.name) ? user.name : 'Unknown Rescuer' }}</h2>
              <div class="d-flex align-center mb-2">
                <v-chip small :color="chipColor" :text-color="primaryColor" class="font-weight-bold mr-2">
                  ID: {{ (rescuerProfile && rescuerProfile.rescuerCode) ? rescuerProfile.rescuerCode : 'N/A' }}
                </v-chip>
                <span class="grey--text text--darken-1 text-subtitle-2">District: {{ (rescuerProfile && rescuerProfile.district) ? rescuerProfile.district : 'N/A' }}</span>
              </div>
              <div class="contact-info">
                <div class="d-flex align-center mb-1">
                  <v-icon small class="mr-2 grey--text text--darken-1">mdi-phone</v-icon>
                  <span class="grey--text text--darken-2">{{ (user && user.phone) ? user.phone : 'N/A' }}</span>
                </div>
                <div class="d-flex align-center">
                  <v-icon small class="mr-2 grey--text text--darken-1">mdi-email</v-icon>
                  <span class="grey--text text--darken-2">{{ (user && user.email) ? user.email : 'N/A' }}</span>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Stats Block -->
        <v-col cols="12" lg="6">
          <v-row class="height-equalizer ma-0" align="stretch">
            <!-- Total Rescues -->
            <v-col cols="12" sm="4" class="pa-1">
              <v-card class="stat-card pa-6 elevation-0 text-center fill-height d-flex flex-column justify-center align-center">
                <v-avatar size="48" :color="avatarColor" class="mb-3">
                  <v-icon :color="primaryColor" size="24">mdi-snake</v-icon>
                </v-avatar>
                <span class="stat-label">Total Rescues</span>
                <span class="stat-value font-weight-bold" :style="{ color: primaryColor }">{{ stats ? stats.totalRescues : 0 }}</span>
              </v-card>
            </v-col>

            <!-- Avg Response Time -->
            <v-col cols="12" sm="4" class="pa-1">
              <v-card class="stat-card pa-6 elevation-0 text-center fill-height d-flex flex-column justify-center align-center">
                <v-avatar size="48" :color="avatarColor" class="mb-3">
                  <v-icon :color="primaryColor" size="24">mdi-clock-outline</v-icon>
                </v-avatar>
                <span class="stat-label">Avg Response Time</span>
                <span class="stat-value font-weight-bold" :style="{ color: primaryColor }">
                  {{ stats && stats.avgResponseTimeMin !== undefined && stats.avgResponseTimeMin !== null ? stats.avgResponseTimeMin + 'm' : 'N/A' }}
                </span>
              </v-card>
            </v-col>

            <!-- Active Status -->
            <v-col cols="12" sm="4" class="pa-1">
              <v-card class="stat-card pa-6 elevation-0 text-center fill-height d-flex flex-column justify-center align-center">
                <v-avatar size="48" :color="avatarColor" class="mb-3">
                  <v-icon :color="primaryColor" size="24">mdi-account-check</v-icon>
                </v-avatar>
                <span class="stat-label">Availability</span>
                <span class="stat-value font-weight-bold" :style="{ color: primaryColor }">
                  {{ (user && user.is_rescuer_Available) ? 'Active' : 'Offline' }}
                </span>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Main Section: Trends and Recent Rescues -->
      <v-row>
        <!-- Recents -->
        <v-col cols="12" lg="8">
          <!-- Recent Rescues Table -->
          <v-card class="table-card elevation-0">
            <div class="card-title-premium pa-4" :style="{ color: primaryColor }">
              <v-icon :color="primaryColor" class="mr-2">mdi-history</v-icon>
              Recent Rescue Logs
            </div>
            <v-simple-table class="modern-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left font-weight-bold">Snake Species</th>
                    <th class="text-left font-weight-bold">District</th>
                    <th class="text-left font-weight-bold">Reported Date</th>
                    <th class="text-left font-weight-bold">Rescued Date</th>
                    <th class="text-center font-weight-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in (stats ? stats.recentRescues : [])"
                    :key="index"
                    class="table-row"
                  >
                    <td class="font-weight-medium text-slate-800">{{ item.snakeName }}</td>
                    <td class="grey--text text--darken-2">{{ item.district }}</td>
                    <td class="grey--text text--darken-1">{{ formatDate(item.reportedAt) }}</td>
                    <td class="grey--text text--darken-1">{{ formatDate(item.rescuedAt) }}</td>
                    <td class="text-center">
                      <v-chip
                        small
                        :color="statusChipColor(item.status)"
                        text-color="white"
                        class="font-weight-bold px-3"
                      >
                        {{ item.status }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="!stats || !stats.recentRescues || stats.recentRescues.length === 0">
                    <td colspan="5" class="text-center py-4 grey--text text--darken-1">
                      No rescue logs found for this rescuer.
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- Charts / Distribution Column -->
        <v-col cols="12" lg="4">
          <!-- Highcharts Column Trends -->
          <v-card class="chart-card pa-6 elevation-0">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="card-title-premium" :style="{ color: primaryColor }">
                <v-icon :color="primaryColor" class="mr-2">mdi-chart-box-outline</v-icon>
                Rescue Trends ({{ selectedTrendYear }})
              </div>
              <div style="width: 110px;">
                <v-select
                  :items="years"
                  v-model="selectedTrendYear"
                  outlined
                  dense
                  hide-details
                  :color="primaryColor"
                  :item-color="isOfficer ? 'indigo' : 'success'"
                  style="font-size: 13px;"
                />
              </div>
            </div>
            <highcharts :options="chartOptions"></highcharts>
          </v-card>
        </v-col>
      </v-row>

    </div>
  </div>
</template>

<script>
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      user: null,
      rescuerProfile: null,
      stats: null,
      chartOptions: {
        chart: {
          type: "column",
          height: 400,
          backgroundColor: "transparent",
          style: {
            fontFamily: "Poppins, sans-serif"
          }
        },
        title: {
          text: ""
        },
        xAxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
          title: {
            text: "Months"
          }
        },
        credits: {
          enabled: false
        },
        yAxis: {
          min: 0,
          title: {
            text: "Total Rescues"
          }
        },
        plotOptions: {
          column: {
            pointWidth: 30,
            dataLabels: {
              enabled: true,
              formatter: function () {
                return this.y.toLocaleString();
              }
            }
          }
        },
        series: [
          {
            name: "Rescues",
            data: [],
            color: "" // Assigned in watch/mounted dynamically
          }
        ],
        exporting: {
          enabled: false
        }
      },
      selectedTrendYear: new Date().getFullYear(),
      years: [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015]
    };
  },
  computed: {
    isOfficer() {
      return this.$route.path.startsWith("/officers");
    },
    primaryColor() {
      return this.isOfficer ? "#312E81" : "#14532D";
    },
    gradientBackground() {
      return this.isOfficer
        ? "linear-gradient(135deg, #1E1B4B 0%, #312E81 55%, #4338CA 100%)"
        : "linear-gradient(135deg, #0A2318 0%, #14532D 55%, #166534 100%)";
    },
    chipColor() {
      return this.isOfficer ? "rgba(49,46,129,0.08)" : "rgba(20,83,45,0.08)";
    },
    avatarColor() {
      return this.isOfficer ? "rgba(49,46,129,0.08)" : "rgba(20,83,45,0.08)";
    },
    avatarText() {
      return this.isOfficer ? "primary--text" : "success--text";
    },
    backPath() {
      return this.isOfficer ? "/officers/topUsers" : "/topUsers";
    }
  },
  watch: {
    primaryColor: {
      immediate: true,
      handler(newColor) {
        this.chartOptions.series[0].color = newColor;
      }
    },
    selectedTrendYear() {
      this.getData();
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.appLoading = true;
      const rescuerId = this.$route.params.id;
      axios({
        method: "GET",
        url: `/operation/rescuer/dashboard/${rescuerId}`,
        headers: {
          "x-auth-token": localStorage.getItem("token")
        },
        params: {
          year: this.selectedTrendYear
        }
      })
        .then((response) => {
          if (response.data.status === true) {
            const data = response.data.data;
            this.user = data.user;
            this.rescuerProfile = data.rescuerProfile;
            this.stats = data.stats;

            // Update Highcharts Series Data
            if (this.stats && this.stats.monthlyStats) {
              this.chartOptions.series[0].data = this.stats.monthlyStats;
            }

            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },

    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "N/A";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    statusChipColor(status) {
      switch (status) {
        case "Released":
          return "#059669";
        case "Rescued":
          return "#10B981";
        case "Approved":
          return "#3B82F6";
        case "Assigned":
          return "#F59E0B";
        default:
          return "#64748B";
      }
    }
  }
};
</script>

<style scoped>
.rd-wrapper {
  background-color: #F8FAFC;
  min-height: 100vh;
}
.rd-header {
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}
.rd-header__title {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
}
.rd-content {
  max-width: 1400px;
  margin: 0 auto;
}
.profile-card, .stat-card, .table-card, .chart-card {
  background: #FFFFFF !important;
  border-radius: 12px !important;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.02) !important;
}
.height-equalizer {
  min-height: 140px;
}
.rescuer-name {
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1E293B;
}
.contact-info {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
}
.stat-label {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748B;
  margin-bottom: 2px;
}
.stat-value {
  font-family: 'Poppins', sans-serif;
  font-size: 26px;
  line-height: 1.2;
}
.card-title-premium {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
}
.modern-table {
  width: 100%;
}
.modern-table th {
  font-family: 'Poppins', sans-serif;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: #FAFBFA;
  height: 48px !important;
  color: #64748B !important;
}
.modern-table td {
  font-family: 'Poppins', sans-serif;
  height: 52px !important;
  font-size: 13px !important;
}
.table-row:hover {
  background-color: #F8FAFC !important;
}
</style>
